import { 
  getAuth, 
  onAuthStateChanged,
  linkWithCredential,
  GoogleAuthProvider,
  EmailAuthProvider,
  User as FirebaseUser,
  AuthCredential,
  signInWithCredential,
  signOut
} from 'firebase/auth';
import app from './firebase';
import { appPathname, inCurrentTree } from '../design/previewPath';
import { FingerprintGenerator } from './fingerprinting';
import { resetSession } from '../lib/auth';

export interface AnonymousUser {
  uid: string;
  isAnonymous: boolean;
  fingerprint: string;
  createdAt: Date;
  lastActive: Date;
}

export interface AuthStateChange {
  user: FirebaseUser | null;
  isAnonymous: boolean;
  fingerprint?: string;
}

export class AnonymousAuthService {
  private static instance: AnonymousAuthService;
  private auth = getAuth(app);
  private currentUser: FirebaseUser | null = null;
  private fingerprint: string | null = null;
  private authStateListeners: ((state: AuthStateChange) => void)[] = [];

  private constructor() {
    this.initializeAuthStateListener();
  }

  static getInstance(): AnonymousAuthService {
    if (!AnonymousAuthService.instance) {
      AnonymousAuthService.instance = new AnonymousAuthService();
    }
    return AnonymousAuthService.instance;
  }

  /**
   * Initialize authentication state listener
   */
  private initializeAuthStateListener(): void {
    onAuthStateChanged(this.auth, async (user) => {
      this.currentUser = user;
      
      if (user) {
        // Update fingerprint for tracking
        if (user.isAnonymous) {
          this.fingerprint = await FingerprintGenerator.getOrCreateUserId();
        }
        
        const authState: AuthStateChange = {
          user,
          isAnonymous: user.isAnonymous,
          fingerprint: this.fingerprint || undefined
        };
        
        this.notifyAuthStateListeners(authState);
      } else {
        this.fingerprint = null;
        this.notifyAuthStateListeners({ user: null, isAnonymous: false });
      }
    });
  }

  /**
   * Subscribe to authentication state changes
   */
  onAuthStateChange(callback: (state: AuthStateChange) => void): () => void {
    this.authStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all auth state listeners
   */
  private notifyAuthStateListeners(state: AuthStateChange): void {
    this.authStateListeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Auth state listener error:', error);
      }
    });
  }

  // Anonymous sign-in removed. Real sessions are required moving forward.

  /**
   * Link anonymous account with Google credential
   */
  async linkWithGoogle(googleCredential: AuthCredential): Promise<FirebaseUser> {
    if (!this.currentUser || !this.currentUser.isAnonymous) {
      throw new Error('No anonymous user to link with');
    }

    try {
      const result = await linkWithCredential(this.currentUser, googleCredential);
      // Ensure mapping on upgrade
      try {
        const email = result.user.email || '';
        if (email) {
          const { ensureEmailUidMapping } = await import('../lib/identity/emailMapping');
          await ensureEmailUidMapping(email);
        }
      } catch {}
      console.log('Anonymous account linked with Google:', result.user.uid);
      return result.user;
    } catch (error: any) {
      console.error('Google account linking failed:', error);
      
      // Handle account-already-exists error
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('An account with this Google profile already exists. Please sign in directly.');
      } else if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists. Please sign in directly.');
      }
      
      throw new Error(`Account linking failed: ${error.message}`);
    }
  }

  /**
   * Link anonymous account with email/password credential
   */
  async linkWithEmailPassword(email: string, password: string): Promise<FirebaseUser> {
    if (!this.currentUser || !this.currentUser.isAnonymous) {
      throw new Error('No anonymous user to link with');
    }

    try {
      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(this.currentUser, credential);
      // Ensure mapping on upgrade
      try {
        const { ensureEmailUidMapping } = await import('../lib/identity/emailMapping');
        await ensureEmailUidMapping(email);
      } catch {}
      console.log('Anonymous account linked with email:', result.user.uid);
      return result.user;
    } catch (error: any) {
      console.error('Email account linking failed:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists. Please sign in directly.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Please choose a stronger password.');
      }
      
      throw new Error(`Account linking failed: ${error.message}`);
    }
  }

  /**
   * Get current user authentication state
   */
  getCurrentAuthState(): AuthStateChange {
    return {
      user: this.currentUser,
      isAnonymous: this.currentUser?.isAnonymous || false,
      fingerprint: this.fingerprint || undefined
    };
  }

  /**
   * Check if user is currently anonymous
   */
  isCurrentUserAnonymous(): boolean {
    return this.currentUser?.isAnonymous || false;
  }

  /**
   * Get current fingerprint
   */
  getCurrentFingerprint(): string | null {
    return this.fingerprint;
  }

  /**
   * Ensure a real authenticated session exists. If not, redirect to /auth and abort.
   */
  async ensureAuthenticated(): Promise<FirebaseUser> {
    // Fast path: if we already have a non-anonymous Firebase user, return it immediately
    const immediate = this.auth.currentUser;
    if (immediate && !immediate.isAnonymous) {
      this.currentUser = immediate;
      return immediate;
    }

    // Otherwise, wait briefly for Firebase auth to settle before deciding to redirect.
    // This avoids redirecting back to /auth during app navigation right after login.
    const awaitedUser = await new Promise<FirebaseUser | null>((resolve) => {
      try {
        const timeoutMs = 3500; // grace period for auth state to propagate
        const unsub = onAuthStateChanged(this.auth, (u) => {
          if (u && !u.isAnonymous) {
            try { unsub(); } catch {}
            resolve(u);
          }
        });
        setTimeout(() => {
          try { unsub(); } catch {}
          resolve(null);
        }, timeoutMs);
      } catch {
        // If listener setup fails, resolve null so we fall through to redirect logic
        resolve(null);
      }
    });

    if (awaitedUser) {
      this.currentUser = awaitedUser;
      return awaitedUser;
    }

    // No authenticated user after waiting: redirect to /auth unless we're already there
    try {
      if (window?.location) {
        // Inside prest0.ai/prest0design/ the address carries the tree's prefix: the page is read without it, and the
        // sign-in it sends a visitor to stays in the tree.
        const path = appPathname(window.location.pathname);
        const isOnAuth = path === '/auth' || path.startsWith('/auth/');
        if (!isOnAuth) {
          const target = `${window.location.origin}${inCurrentTree('/auth')}`;
          if (window.location.href !== target) {
            window.location.replace(target);
          }
        }
      }
    } catch {}

    const error = new Error('Authentication required');
    console.error('Failed to ensure authentication (real session required):', error);
    throw error;
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      this.fingerprint = null;
      console.log('User signed out successfully');
      try { await resetSession('explicit_sign_out'); } catch {}
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }

  /**
   * Get current user ID (Firebase UID)
   */
  getCurrentUserId(): string | null {
    return this.currentUser?.uid || null;
  }

  /**
   * Check if authentication is ready
   */
  isAuthReady(): boolean {
    return this.auth.currentUser !== undefined;
  }
} 